import React, { useState, useEffect } from 'react';
import { Stack, Box, Typography } from '@mui/material';
import { GroupsOutlined, InfoOutlined } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { updateStep } from '../../../state';
import { isAutoAssignConfig, createDefaultAssignmentRule, AssignmentRule, AssignmentRuleType } from '../../../../../services/WorkflowService';
import { useGetAssignmentRuleTypesQuery } from '../../../../../services/WorkflowService';
import { useGetActiveTeamsQuery } from '../../../../../services/TeamService';
import { useUserSearch } from '../../../hooks/useUserSearch';
import { GraphUser } from '../../../../../utils/MsGraphApiCall';
import { UserSearchAutocomplete } from '../common';
import { RuleTypeSelector } from './RuleTypeSelector';
import { TeamSelector } from './TeamSelector';
import { UserPoolAutocomplete } from './UserPoolAutocomplete';
import { useWorkflowValidationContext, getStepConfigError } from '../../shared';

interface AutoAssignmentConfigProps {
  index: number;
}

// Builds a placeholder GraphUser from a stored assignment rule target, tolerating legacy rules
// that only ever persisted one of targetUser/targetUserName.
const buildPlaceholderUser = (targetUser: string, targetUserName: string): GraphUser => ({
  id: targetUser,
  displayName: targetUserName || targetUser,
  mail: (targetUserName || targetUser.includes('@')) ? targetUser : '',
  userPrincipalName: targetUser,
  accountEnabled: true,
});

export const AutoAssignmentConfig: React.FC<AutoAssignmentConfigProps> = ({ index }) => {
  const dispatch = useAppDispatch();
  const step = useAppSelector((state) => state.workflow.currentWorkflow?.steps[index]);
  const { data: ruleTypes, isLoading: ruleTypesLoading } = useGetAssignmentRuleTypesQuery();
  const { data: teams, isLoading: teamsLoading } = useGetActiveTeamsQuery();
  const { errors, showErrors } = useWorkflowValidationContext();
  const configError = getStepConfigError(errors, showErrors, index);

  const { results: users, isLoading: userSearchLoading, query: searchQuery, setQuery: setSearchQuery, reset: resetUserSearch } = useUserSearch();
  const [selectedUser, setSelectedUser] = useState<GraphUser | null>(null);

  const { results: poolUsers, isLoading: poolSearchLoading, query: poolSearchQuery, setQuery: setPoolSearchQuery, reset: resetPoolSearch } = useUserSearch();
  const [selectedPoolUsers, setSelectedPoolUsers] = useState<GraphUser[]>([]);

  const rawConfig = step?.configuration;
  const assignmentRule = rawConfig && isAutoAssignConfig(rawConfig)
    ? rawConfig.assignmentRule
    : createDefaultAssignmentRule();
  const ruleType = assignmentRule.type || 'Specific';

  useEffect(() => {
    if (assignmentRule.targetUser && !selectedUser) {
      setSelectedUser(buildPlaceholderUser(assignmentRule.targetUser, assignmentRule.targetUserName));
    }
  }, [assignmentRule.targetUser, assignmentRule.targetUserName, selectedUser]);

  useEffect(() => {
    if (assignmentRule.userPool?.length > 0 && selectedPoolUsers.length === 0) {
      const existingPool = assignmentRule.userPool.map((u: string) => ({
        id: u,
        displayName: u,
        mail: u.includes('@') ? u : '',
        userPrincipalName: u,
        accountEnabled: true,
      }));
      setSelectedPoolUsers(existingPool);
    }
  }, [assignmentRule.userPool, selectedPoolUsers.length]);

  const updateConfig = (ruleUpdates: Partial<AssignmentRule>) => {
    if (!step) return;
    dispatch(
      updateStep({
        index,
        updates: {
          configuration: {
            ...step.configuration,
            assignmentRule: { ...assignmentRule, ...ruleUpdates },
          },
        },
      })
    );
  };

  const handleRuleTypeChange = (newType: AssignmentRuleType) => {
    updateConfig({
      type: newType, targetUser: '', targetUserName: '', targetTeam: '',
      userPool: [], requiredSkills: [], maxWorkload: 10,
    });
    setSelectedUser(null);
    setSelectedPoolUsers([]);
    resetUserSearch();
    resetPoolSearch();
  };

  const handleUserSelect = (_event: React.SyntheticEvent, user: GraphUser | null) => {
    setSelectedUser(user);
    // targetUser is the canonical, unique identifier (email); targetUserName is display-only.
    updateConfig({
      targetUser: user ? user.mail || user.displayName : '',
      targetUserName: user ? user.displayName || user.mail : ''
    });
  };

  const handlePoolUsersChange = (_event: React.SyntheticEvent, newUsers: GraphUser[]) => {
    setSelectedPoolUsers(newUsers);
    updateConfig({ userPool: newUsers.map((u) => u.displayName || u.mail) });
  };

  if (!step) return null;

  return (
    <Stack
      spacing={2}
      sx={{ mt: 1, p: 2, backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <GroupsOutlined sx={{ color: '#64748b', fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Assignment Configuration
        </Typography>
      </Stack>

      <RuleTypeSelector
        value={ruleType}
        ruleTypes={ruleTypes}
        isLoading={ruleTypesLoading}
        onChange={handleRuleTypeChange}
      />

      {ruleType === 'Specific' && (
        <UserSearchAutocomplete
          label="Target User"
          placeholder="Search for a user..."
          value={selectedUser}
          users={users}
          isLoading={userSearchLoading}
          searchQuery={searchQuery}
          onInputChange={setSearchQuery}
          onSelect={handleUserSelect}
          error={!!configError}
          helperText={configError}
        />
      )}

      {ruleType === 'Team' && (
        <TeamSelector
          value={assignmentRule.targetTeam || ''}
          teams={teams}
          isLoading={teamsLoading}
          onChange={(teamId) => updateConfig({ targetTeam: teamId })}
          error={!!configError}
          helperText={configError}
        />
      )}

      {ruleType === 'RoundRobin' && (
        <UserPoolAutocomplete
          value={selectedPoolUsers}
          users={poolUsers}
          isLoading={poolSearchLoading}
          searchQuery={poolSearchQuery}
          onInputChange={setPoolSearchQuery}
          onChange={handlePoolUsersChange}
          error={!!configError}
          helperText={configError}
        />
      )}

      {(ruleType === 'Skillset' || ruleType === 'Workload') && (
        <Box
          sx={{ p: 2, backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 1 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlined sx={{ color: '#ea580c', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: '#9a3412' }}>
              {ruleType}-based assignment is coming soon. Please use Specific, Team, or RoundRobin
              assignment for now.
            </Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default AutoAssignmentConfig;
